import * as preact from "preact";
import PropTypes from "prop-types";

import classname from "../../../foundation/utils/classname";
import noderef from "../../../foundation/utils/noderef";
import { scrollIfNecessary } from "../../../foundation/utils/platform";
import zip from "../../../foundation/utils/zip";

class Node extends preact.Component {
    constructor(props) {
        super(props);

        this.handleBaseRef = this.handleBaseRef.bind(this);
        this.handleChevronClick = this.handleChevronClick.bind(this);
        this.handleNodeClick = this.handleNodeClick.bind(this);
        this.handleNodeHover = this.handleNodeHover.bind(this);
    }

    handleBaseRef(el) {
        const { baseRef, mirror } = this.props;
        noderef(this, "base")(el);
        if (!mirror && baseRef) {
            baseRef(el);
        }
    }

    handleChevronClick(ev) {
        const { id, onChevronClick } = this.props;

        ev.stopPropagation();
        onChevronClick(id);
    }

    handleNodeClick(ev) {
        const { id, onNodeSelect } = this.props;

        ev.stopPropagation();
        onNodeSelect(id, { isSelectedWithClick: true });
    }

    handleNodeHover(ev) {
        const { id, onNodeHover } = this.props;

        ev.stopPropagation();
        onNodeHover(id);
    }

    componentDidMount() {
        const { allowScrollOnMount, selected } = this.props;

        if (selected && allowScrollOnMount) {
            scrollIfNecessary(this.nodeRefs.base);
        }
    }

    shouldComponentUpdate(nextProps) {
        const { children } = this.props;
        const { children: nextChildren } = nextProps;
        const childrenArray = preact.toChildArray(children);
        const nextChildrenArray = preact.toChildArray(nextChildren);

        if (childrenArray.length !== nextChildrenArray.length) {
            return true;
        }

        for (const [child1, child2] of zip(childrenArray, nextChildrenArray)) {
            if (child1 !== child2) {
                return true;
            }
        }

        return (
            this.props.id !== nextProps.id ||
            this.props.title !== nextProps.title ||
            this.props.level !== nextProps.level ||
            this.props.numberOfChildren !== nextProps.numberOfChildren ||
            this.props.collapsed !== nextProps.collapsed ||
            this.props.selected !== nextProps.selected ||
            this.props.class !== nextProps.class ||
            this.props.dragging !== nextProps.dragging ||
            this.props.targetPosition !== nextProps.targetPosition
        );
    }

    componentDidUpdate(previousProps) {
        const { selected } = this.props;

        if (selected && !previousProps.selected) {
            scrollIfNecessary(this.nodeRefs.base);
        }
    }

    render() {
        const {
            children,
            class: cssClass,
            chevronIcon: ChevronIcon,
            extraPadding,
            unitPadding,
            internalNodePadding,
            leafNodePadding,
            title,
            level,
            numberOfChildren,
            collapsed,
            selected,
            dragging,
            targetPosition,
            mirror
        } = this.props;

        const classes = classname("node", "body-1", cssClass, targetPosition, { selected, mirror, dragging });
        const chevronClasses = classname("chevron", { collapsed });
        const leafNodePaddingValue = leafNodePadding || unitPadding * 2;
        const paddingLeft = (mirror ? 0 : extraPadding) +
            level * unitPadding +
            (!numberOfChildren ? leafNodePaddingValue : internalNodePadding);

        return (
            <>
                {targetPosition === "top" && <span class="dragDropNode placeholder" style={{ left: paddingLeft }}/>}
                <div
                    class={classes}
                    style={{ paddingLeft }}
                    ref={this.handleBaseRef}
                    onClick={this.handleNodeClick}
                    onMouseEnter={this.handleNodeHover}>
                    {
                        !!numberOfChildren &&
                        <button class={chevronClasses} onClick={this.handleChevronClick}><ChevronIcon/></button>
                    }
                    <span class="nodeName ellipsis noSelect" title={title}>{children}</span>
                </div>
                {targetPosition === "bottom" && <span class="dragDropNode placeholder" style={{ left: paddingLeft }}/>}
            </>
        );
    }
}

const NodeMirror = ({ ...props }) => <Node {...props} mirror collapsed/>;

Node.propTypes = {
    /** CSS class(es) to be passed to the node. */
    class: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    /** The icon component to be used for the chevron */
    chevronIcon: PropTypes.elementType,
    /** Extra left padding be added to the node. */
    extraPadding: PropTypes.number,
    /** Extra left padding to be applied per level of depth in the tree. */
    unitPadding: PropTypes.number,
    /** Left padding to be applied to internal nodes. */
    internalNodePadding: PropTypes.number,
    /** Left padding to be applied to leaf nodes. */
    leafNodePadding: PropTypes.number,
    /** Unique id of the node. */
    id: PropTypes.string.isRequired,
    /** Nesting level of the node. */
    level: PropTypes.number.isRequired,
    /** Content to be rendered in the node. */
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.element
    ]).isRequired,
    /** Text to be displayed in the tooltip if node content is ellipsized. */
    title: PropTypes.string.isRequired,
    /** Number of child nodes */
    numberOfChildren: PropTypes.number.isRequired,
    /** Whether the children of a node is collapsed, thus should the chevron be rotated. */
    collapsed: PropTypes.bool.isRequired,
    /** Whether the view should be scrolled to make this node visible if it is selected when it mounts */
    allowScrollOnMount: PropTypes.bool.isRequired,
    /** Whether the node is selected. */
    selected: PropTypes.bool.isRequired,
    /** Whether a node is dragging */
    dragging: PropTypes.bool.isRequired,
    /** Whether component is a mirror node. */
    mirror: PropTypes.bool,
    /** Whether a placeholder should be placed to a position */
    targetPosition: PropTypes.oneOf(["", "into", "top", "bottom"]),
    /** Callback to be invoked when a node is clicked. */
    onNodeSelect: PropTypes.func.isRequired,
    /** Callback to be invoked when the chevron of a node is clicked. */
    onChevronClick: PropTypes.func.isRequired
};

Node.defaultProps = {
    extraPadding: 0,
    unitPadding: 12,
    internalNodePadding: 8
};

export { NodeMirror };
export default Node;
